import { useCallback, useEffect, useState } from 'react';
import {
  getAllArticles,
  getFavoriteArticleIds,
  getRecentArticleIds,
  getWikiArticleTypes,
  getWikiCategories,
  getWikiViewStats,
} from '../modules/knowledge/wiki/wiki.api';
import { articleTypes, categories, initialArticles, slugify } from '../modules/knowledge/wiki/wiki.constants';
import { getWikiErrorMessage } from '../modules/knowledge/wiki/wiki.errors';

const fallbackTypes = () => articleTypes.map((name) => ({ name, code: slugify(name) }));

export default function useWikiData() {
  const [articles, setArticles] = useState(initialArticles);
  const [wikiCategories, setWikiCategories] = useState(categories);
  const [wikiArticleTypes, setWikiArticleTypes] = useState(fallbackTypes);
  const [favorites, setFavorites] = useState([]);
  const [recentIds, setRecentIds] = useState([]);
  const [viewStats, setViewStats] = useState([]);
  const [wikiLoading, setWikiLoading] = useState(true);
  const [wikiError, setWikiError] = useState(null);
  const [wikiPermissionDenied, setWikiPermissionDenied] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setWikiLoading(true);
        const [articleData, categoryData, typeData, favoriteData, recentData, statsData] = await Promise.all([
          getAllArticles(), getWikiCategories({ includeInactive: true }), getWikiArticleTypes(),
          getFavoriteArticleIds(), getRecentArticleIds(), getWikiViewStats(),
        ]);
        if (!active) return;
        setArticles(Array.isArray(articleData) && articleData.length ? articleData : initialArticles);
        setWikiCategories(Array.isArray(categoryData) && categoryData.length ? categoryData : categories);
        setWikiArticleTypes(Array.isArray(typeData) && typeData.length ? typeData : fallbackTypes());
        setFavorites(Array.isArray(favoriteData) ? favoriteData : []);
        setRecentIds(Array.isArray(recentData) ? recentData : []);
        setViewStats(Array.isArray(statsData) ? statsData : []);
        setWikiError(null);
        setWikiPermissionDenied(false);
      } catch (error) {
        if (!active) return;
        setWikiPermissionDenied(error.status === 403);
        setWikiError(getWikiErrorMessage(error, 'Impossible de charger le wiki depuis l API.'));
        setArticles(initialArticles); setWikiCategories(categories); setWikiArticleTypes(fallbackTypes());
        setFavorites([]); setRecentIds([]); setViewStats([]);
      } finally {
        if (active) setWikiLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [reloadKey]);

  return {
    articles, setArticles, wikiCategories, setWikiCategories, wikiArticleTypes,
    favorites, setFavorites, recentIds, setRecentIds, viewStats, wikiLoading,
    wikiError, setWikiError, wikiPermissionDenied, setWikiPermissionDenied, reload,
  };
}
